import 'symbol-observable';
import './wdyr';
import '../css/app.scss';

import React, { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';

import 'leaflet/dist/leaflet.css';
import {
  Router,
  Switch,
  Route,
  Redirect,
  RouteProps,
  RouteComponentProps,
} from 'react-router-dom-v5';
import { Provider } from 'react-redux';
import CsfrTokenFetcher from 'spw-components/csfr-token-fetcher';
import { PersistGate } from 'redux-persist/integration/react';
import { store, persistor } from 'spw-redux/store';
import { ThemeProvider, Theme, StyledEngineProvider } from '@mui/material';
import ExpiredPasswordResetLink from 'spw-components/login/expired-password-reset-link';
import TokenExpirationModal from 'spw-components/dialogs/token-expiration-modal';
import RouterActionListener from 'spw-components/router-action-listener';
import * as constants from 'spw-constants';
import { initPerformanceObserver } from 'spw-shared/performance';
import Header from 'spw-components/header';
import SendEmail from 'spw-components/login/send-email';
import ResetPassword from 'spw-components/login/reset-password';
import ExportChooser from 'spw-shared/reports/export-chooser';
import SSOLogin from 'spw-components/login/sso-login';
import RecoveryEmailSent from 'spw-components/login/recovery-email-sent';
import Lockout from 'spw-components/login/lockout';
import { setPreviouslyVisitedBenchmarkProjectId } from 'spw-redux/modules/projects/actions';
import { logout as logoutAction } from 'spw-redux/modules/auth/actions';
import NotFound from './not-found-page';
import { theme } from './theme/index';
import history from './history';
import './types';
import { initSentry } from './sentry';

type PreloadableRoute = RouteProps & {
  key: string;
  fetchComponent?: () => Promise<{ default: React.ComponentType<any> }>;
};

declare module '@mui/styles/defaultTheme' {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface DefaultTheme extends Theme {}
}

const routes = [
  {
    exact: true,
    key: 'projects',
    path: [
      `${constants.PROJECTS_ROUTE}`,
      `${constants.PROJECTS_ROUTE}/requests-to-join`,
    ],
    fetchComponent: () =>
      import(/* webpackChunkName: 'projects' */ './projects'),
  },
  {
    key: 'projects/esa',
    path: `${constants.PROJECTS_ROUTE}/${
      constants.PROJECT_TYPE_TO_PATH[constants.EXISTING_SITE_ANALYSIS]
    }/:projectId`,
    fetchComponent: () =>
      import(/* webpackChunkName: 'esa_projects' */ 'spw-projects/esa/index'),
  },
  {
    key: 'projects/programming',
    path: `${constants.PROJECTS_ROUTE}/${
      constants.PROJECT_TYPE_TO_PATH[constants.PROGRAMMING]
    }/:projectId`,
    fetchComponent: () =>
      import(
        /* webpackChunkName: 'programming_projects' */ 'spw-programming-projects/index'
      ),
  },
  {
    exact: true,
    key: 'esa-home',
    path: '/existing-site-analysis',
    children: <Redirect to="/home" />,
  },
  {
    exact: true,
    key: 'prog-home',
    path: '/programming',
    children: <Redirect to="/home" />,
  },
  {
    exact: true,
    key: 'home',
    path: '/home',
    fetchComponent: () =>
      import(/* webpackChunkName: 'home' */ 'spw-components/home'),
  },
  {
    exact: true,
    key: 'account',
    path: '/account',
    fetchComponent: () => import(/* webpackChunkName: 'account' */ './account'),
  },
  {
    exact: true,
    key: 'clients',
    path: '/clients',
    fetchComponent: () => import(/* webpackChunkName: 'clients' */ './clients'),
  },
  {
    exact: true,
    key: 'benchmark-reports',
    path: '/benchmark-reports',
    fetchComponent: () =>
      import(/* webpackChunkName: 'benchmark_reports' */ './benchmark-reports'),
  },
  {
    exact: true,
    key: 'create-benchmark-report',
    path: '/benchmark-reports/new',
    fetchComponent: () =>
      import(
        /* webpackChunkName: 'create_benchmark_report' */ './benchmark-reports/create-benchmark-report'
      ),
  },
  {
    key: 'benchmark-report-profile',
    path: '/benchmark-reports/:reportId/profile',
    fetchComponent: () =>
      import(
        /* webpackChunkName: 'benchmark_report_profile' */ './benchmark-reports/detail/profile'
      ),
  },
  {
    exact: true,
    key: 'benchmark-report-projects',
    path: '/benchmark-reports/:reportId/projects',
    fetchComponent: () =>
      import(
        /* webpackChunkName: 'benchmark_report_projects' */ './benchmark-reports/detail/projects'
      ),
  },
  {
    exact: true,
    key: 'bechmark-report-summary',
    path: '/benchmark-reports/:reportId/summary',
    fetchComponent: () =>
      import(
        /* webpackChunkName: 'benchmark_report_detail_summary' */ './benchmark-reports/detail/summary'
      ),
  },
  {
    exact: true,
    key: 'benchmark-report-summary-preview',
    path: '/benchmark-reports/new/preview',
    fetchComponent: () =>
      import(
        /* webpackChunkName: 'benchmark_report_detail_preview' */ './benchmark-reports/summary/preview'
      ),
  },
  {
    key: 'client-detail',
    path: '/clients/:clientId/:tab?',
    fetchComponent: () =>
      import(/* webpackChunkName: 'client_detail' */ './clients/client-detail'),
  },
  {
    exact: true,
    key: 'teams',
    path: ['/teams', '/teams/requests-to-join'],
    fetchComponent: () => import(/* webpackChunkName: 'teams' */ './teams'),
  },
  {
    key: 'team-detail',
    path: '/teams/:teamId/:tab?',
    fetchComponent: () =>
      import(/* webpackChunkName: 'team_detail' */ './teams/team-detail'),
  },
  {
    exact: true,
    key: 'users',
    path: '/users',
    fetchComponent: () => import(/* webpackChunkName: 'users' */ './users'),
  },
  {
    key: 'user-detail',
    path: '/users/:userId',
    fetchComponent: () =>
      import(/* webpackChunkName: 'user_detail' */ './users/user-detail'),
  },
] as PreloadableRoute[];

initSentry();

declare global {
  interface Window {
    Cypress: any;
    store: any;
  }

  interface Array<T> {
    findLastIndex(
      predicate: (value: T, index: number, obj: T[]) => unknown,
      thisArg?: any
    ): number;
  }
}

if (window.Cypress) {
  window.store = store;
}

history.listen((location) => {
  if (
    !/\/projects\/esa\/\d+\/*/.test(location.pathname) &&
    store.getState().projectsReducer.previouslyVisitedBenchmarkProjectId
  ) {
    store.dispatch(setPreviouslyVisitedBenchmarkProjectId(null));
  }

  if (!window.gtag || !window.trackingId) {
    return;
  }
  window.gtag('config', window.trackingId, { page_path: location.pathname });
});

// Catch errors since some browsers throw when using the new `type` option.
// https://bugs.webkit.org/show_bug.cgi?id=209216
try {
  // Create the performance observer.
  initPerformanceObserver();
} catch (e) {
  // Do nothing if the browser doesn't support this API.
}

const lazyRetry = (
  fetchComponent: PreloadableRoute['fetchComponent']
): ReturnType<PreloadableRoute['fetchComponent']> => {
  return new Promise((resolve, reject) => {
    const hasRefreshed = JSON.parse(
      window.sessionStorage.getItem('retry-lazy-refreshed') || 'false'
    );

    fetchComponent()
      .then((component) => {
        window.sessionStorage.setItem('retry-lazy-refreshed', 'false');
        resolve(component);
      })
      // eslint-disable-next-line consistent-return
      .catch((error) => {
        if (!hasRefreshed) {
          // not been refreshed yet
          window.sessionStorage.setItem('retry-lazy-refreshed', 'true'); // we are now going to refresh
          return window.location.reload(); // refresh the page
        }
        reject(error);
      });
  });
};

const App = () => {
  const [preloadableRoutes, setPreloadableRoutes] = useState(routes);
  const isMounted = useRef(false);

  useEffect(() => {
    setPreloadableRoutes(
      preloadableRoutes.map((route) => {
        const copy = { ...route };
        const { fetchComponent } = copy;
        if (fetchComponent) {
          const preloadedComponent = lazyRetry(fetchComponent);
          copy.component = lazy(() => preloadedComponent);
        }
        return copy;
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleNewToken = (e: StorageEvent) => {
      if (e.key === 'api_token_v2' && e.oldValue !== e.newValue) {
        if (!e.newValue) {
          store.dispatch(logoutAction());
          return;
        }

        if ((!e.oldValue && e.newValue) || (e.oldValue && e.newValue)) {
          window.location.reload();
        }
      }
    };

    window.addEventListener('storage', handleNewToken);
    return function cleanup() {
      if (isMounted.current)
        window.removeEventListener('storage', handleNewToken);
      else isMounted.current = true;
    };
  }, []);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <Provider store={store}>
          <PersistGate persistor={persistor} loading={null}>
            <CsfrTokenFetcher />
            <Router history={history}>
              <RouterActionListener>
                <Sentry.ErrorBoundary
                  key={history.location.pathname}
                  beforeCapture={(scope, error) => {
                    console.log(error);
                    scope.setTag('location', history.location.pathname);
                  }}
                >
                  <Suspense fallback={<div />}>
                    <Route
                      render={({ location }) => {
                        return location.pathname.startsWith(
                          '/download'
                        ) ? null : (
                          <>
                            <Header />
                            <TokenExpirationModal />
                          </>
                        );
                      }}
                    />

                    <Switch>
                      <Route path="/downloads" component={ExportChooser} />
                      <Route exact path="/">
                        <Redirect
                          to={
                            store.getState().authReducer?.apiToken
                              ? '/home'
                              : '/login'
                          }
                        />
                      </Route>
                      <Route exact path="/login" component={SSOLogin} />
                      <Route
                        exact
                        path="/auth/recovery-email-sent"
                        component={RecoveryEmailSent}
                      />
                      <Route
                        exact
                        path="/auth/password-reset"
                        component={SendEmail}
                      />
                      <Route exact path="/auth/lockout" component={Lockout} />
                      <Route
                        exact
                        path="/auth/update-password/:token"
                        component={ResetPassword}
                      />
                      <Route
                        exact
                        path="/auth/expired-password-reset-link/:oldToken?"
                        component={ExpiredPasswordResetLink}
                      />
                      {/* Return these routes as a list,  otherwise
                    the 404 page won't be selected if a nonexistent page is requested
                */}
                      {/* <Route exact={true} path="/home" component={Home} /> */}
                      {preloadableRoutes.map(
                        ({ key, component: Component, ...rest }) => (
                          <Route
                            key={key}
                            component={(props: RouteComponentProps) =>
                              Component ? <Component {...props} /> : null
                            }
                            {...rest}
                          />
                        )
                      )}
                      <Route path="*" component={NotFound} />
                    </Switch>
                  </Suspense>
                </Sentry.ErrorBoundary>
              </RouterActionListener>
            </Router>
          </PersistGate>
        </Provider>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

const container = document.getElementById('react-container');
const root = createRoot(container);
root.render(<App />);
